/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * RGBARRY-CUSTOMIZATION (W-23754187): canonical legacy-PDP-URL derivation.
 *
 * Production baggallini.com (SFRA) serves product detail pages at
 * `/{decorative-slug}/{PRODUCT_ID}.html` and 301-redirects any *non-canonical* variant of
 * that URL — a wrong slug, or extra path segments — to the single canonical slug. e.g.
 *
 *   /lexington-backpack/anything/LEX217-BG-B1222-NS.html   ─┐
 *   /wrong-slug/LEX217-BG-B1222-NS.html                    ─┼─► /lexington-backpack/LEX217-BG-B1222-NS.html
 *   /LEXington-Backpack/LEX217-BG-B1222-NS.html            ─┘
 *
 * On SFRA this 301 is produced by the platform's SEO-URL engine (upstream of the
 * `Product-Show` controller), not by cartridge code. Storefront Next has no such engine, so
 * we reproduce it in the PDP loader using the one field the SCAPI Shopper Products `Product`
 * schema exposes that carries a storefront URL: **`slugUrl`** ("the complete link to this
 * product's storefront page"), which the platform populates from the same Business-Manager
 * URL rules that generate production's URLs.
 *
 * `slugUrl` is deliberately NOT used verbatim. Salesforce's own Storefront Next reference
 * storefront warns it can carry the *internal* Managed-Runtime / proxy origin (an origin-leak
 * hazard) and its exact shape varies by site/locale prefix
 * (`https://<host>/s/<site>/<locale>/<slug>/<id>.html`). So we parse out only the **decorative
 * slug segment** — the path segment immediately before the final `<something>.html` segment —
 * and rebuild a clean root-relative `/<slug>/<productId>.html`, taking the productId from the
 * REQUEST (not from `slugUrl`) so a `?pid=<variant>` request keeps its master id in the path.
 *
 * When the canonical slug can't be determined — `slugUrl` absent (SEO URLs disabled for the
 * site/locale) or not in the SEO `.html` form (e.g. a `Product-Show?pid=` controller link) —
 * this returns `null`, meaning "render the page as-is". Redirecting on a guess would risk
 * sending a real, indexed URL to a 404 or into a loop; rendering as-is is the safe fallback.
 *
 * NOTE (single-slug assumption): baggallini PDPs use exactly ONE decorative slug segment
 * (`/swift-crescent/SHB986-BG.html`), so we take a single segment. The storefront runs with
 * `app.url.prefix = '/'` (single site, single locale), so the request pathname the caller
 * compares this against has no `/:siteId/:localeId` prefix to account for.
 *
 * @param slugUrl - The product's `slugUrl` from SCAPI (may be absolute or relative, or undefined).
 * @param productId - The product id from the REQUEST path (`params.productId`), used to build the
 *   canonical path so variant (`?pid=`) requests keep the master id in the URL.
 * @returns The canonical root-relative path `/<slug>/<productId>.html` (no query string), or
 *   `null` when it cannot be determined and the page should be rendered as-is.
 */
/**
 * Throwaway base origin for parsing a possibly-relative `slugUrl` with `new URL()`. Uses the
 * reserved `.invalid` TLD (RFC 6761 §6.4), which is guaranteed never to resolve to a real host —
 * so it reads unambiguously as a placeholder parse origin, not as a claim that any input is
 * invalid. Only the resulting `.pathname` is ever used; the origin is always discarded.
 */
const PLACEHOLDER_PARSE_BASE = 'https://placeholder.invalid';

export function getCanonicalProductPath(slugUrl: string | undefined | null, productId: string): string | null {
    if (!slugUrl || !productId) {
        return null;
    }

    let pathname: string;
    try {
        // `slugUrl` may be absolute (carrying an internal origin) or already relative — resolve
        // against a throwaway base so both parse. Only the pathname is trusted; the origin is
        // discarded, which is exactly the origin-leak mitigation the reference storefront calls for.
        // The base uses the reserved `.invalid` TLD (RFC 6761 §6.4): it can never resolve to a real
        // host, so it's an unmistakable throwaway parse origin — NOT a judgment that `slugUrl` is
        // malformed. The whole origin is dropped; we keep `.pathname` only.
        pathname = new URL(slugUrl, PLACEHOLDER_PARSE_BASE).pathname;
    } catch {
        return null;
    }

    // Need at least `<slug>/<id>.html`; the last segment must be the `.html` leaf, and the
    // segment before it is the decorative slug. Anything else (a controller path, a bare slug)
    // is not the SEO form we canonicalize to.
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length < 2) {
        return null;
    }
    const lastSegment = segments[segments.length - 1];
    if (!lastSegment.toLowerCase().endsWith('.html')) {
        return null;
    }
    const slug = segments[segments.length - 2];
    if (!slug) {
        return null;
    }

    return `/${slug}/${productId}.html`;
}

/**
 * RGBARRY-CUSTOMIZATION (W-23754187): detect a decorated legacy PDP path and extract its product id.
 *
 * The dedicated `/:slug/:productId.html` route only matches a TWO-segment path. Production also
 * serves — and search engines have indexed — PDP URLs with EXTRA decorative segments, e.g.
 * `/lexington-backpack/anything/LEX217-BG-B1222-NS.html`. Those fall through to the root catch-all
 * (`_app.$`), which uses this helper to recognize them: a path with 3+ segments whose final segment
 * is `<productId>.html`. The extracted id then drives a product fetch + `getCanonicalProductPath`
 * to 301 down to the single canonical `/<slug>/<productId>.html` (a 2-segment URL that then matches
 * the dedicated route and renders) — mirroring SFRA's platform-level slug canonicalization.
 *
 * Returns the product id (final segment minus the `.html` suffix), or `null` when the path is not a
 * 3+-segment `.html` leaf (2-segment PDPs are handled by the dedicated route; other paths — bare
 * category/content slugs, BM redirects — must keep flowing to `getUrlMapping` unchanged).
 *
 * @param pathname - Request pathname, already stripped of any `/:siteId/:localeId` prefix.
 */
/**
 * RGBARRY-CUSTOMIZATION (W-23789029, outbound links): build a canonical PDP path for an INTERNAL
 * `<Link>` when we only have the product NAME, not `slugUrl`.
 *
 * PLP tiles — the dominant internal PDP link — are driven by `ProductSearchHit`, which does NOT
 * carry `slugUrl` (see product-schema.ts). So `getCanonicalProductPath` (which parses `slugUrl`)
 * can't run on the search/browse path, and the default `/product/:productId` route would emit a
 * non-canonical URL that doesn't match production's `/{slug}/{id}.html`.
 *
 * baggallini's canonical slug IS the slugified product name — verified against prod
 * (`Via Large Tote` → `/via-large-tote/VAT152-BG.html`) — so we derive the slug from the name and
 * emit the canonical shape directly, matching production's URLs with NO redirect on the hot browse
 * path. This is best-effort: if a derived slug ever differs from the platform's canonical slug, the
 * dedicated `/:slug/:productId.html` route 301-self-heals it (from the real `slugUrl` it fetches).
 * When no usable name is given, returns `null` so the caller falls back to `/product/:productId`.
 */
export function slugifyProductName(name: string): string {
    return name
        .normalize('NFKD') // decompose accented chars so the diacritic becomes a combining mark
        .replace(/\p{M}/gu, '') // strip combining marks (NFKD split accents into base + mark)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-') // any run of non-alphanumerics collapses to one hyphen
        .replace(/^-+|-+$/g, ''); // trim leading/trailing hyphens
}

/**
 * Build the canonical root-relative PDP path `/{slug}/{productId}.html` from a product name, or
 * `null` when it can't be built (missing id, or a name that slugifies to empty). See
 * `slugifyProductName` for the baggallini-specific rationale and the 301 self-heal fallback.
 */
export function buildInternalProductPath(productId: string | undefined, productName?: string | null): string | null {
    if (!productId) {
        return null;
    }
    const slug = productName ? slugifyProductName(productName) : '';
    return slug ? `/${slug}/${productId}.html` : null;
}

export function extractDecoratedPdpProductId(pathname: string): string | null {
    const segments = pathname.split('/').filter(Boolean);
    // 2-segment `/slug/id.html` is the dedicated route's job; only 3+ segments reach the catch-all
    // as a decorated PDP. Fewer than 3 segments here is not a decorated-PDP path.
    if (segments.length < 3) {
        return null;
    }
    const lastSegment = segments[segments.length - 1];
    if (!lastSegment.toLowerCase().endsWith('.html')) {
        return null;
    }
    const productId = lastSegment.slice(0, -'.html'.length);
    return productId.length > 0 ? productId : null;
}
